/**
 * Back-in-Stock notification component.
 *
 * Handles the "Benachrichtige mich" flow:
 * 1. User clicks trigger button → email form appears
 * 2. User submits email → stored in localStorage (keyed by product+variant)
 *    and sent to a configurable endpoint or stored for later retrieval
 * 3. Success message shown
 *
 * Storage: localStorage key "dufftie_back_in_stock" holds an array of
 * { email, productHandle, variantId, productTitle, timestamp }
 *
 * If a Shopify app endpoint is available (e.g. Klaviyo), set
 * window.__bisEndpoint = '/apps/klaviyo/back-in-stock/subscribe'
 * and the component will POST there instead.
 */

const STORAGE_KEY = 'dufftie_back_in_stock';

class BackInStockComponent extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[ref="trigger"]');
    this.formWrapper = this.querySelector('[ref="formWrapper"]');
    this.form = this.querySelector('[ref="form"]');
    this.emailInput = this.querySelector('[ref="emailInput"]');
    this.submitButton = this.querySelector('[ref="submitButton"]');
    this.success = this.querySelector('[ref="success"]');

    if (!this.trigger || !this.form) return;

    this.productHandle = this.dataset.productHandle;
    this.variantId = this.dataset.variantId;
    this.productTitle = this.dataset.productTitle;

    // Check if already subscribed
    if (this._isAlreadySubscribed()) {
      this._showSuccess();
      return;
    }

    this.trigger.addEventListener('click', this._onTriggerClick.bind(this));
    this.form.addEventListener('submit', this._onSubmit.bind(this));

    // Close form on Escape
    this.formWrapper?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this._hideForm();
        this.trigger.focus();
      }
    });
  }

  _onTriggerClick() {
    this.trigger.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.formWrapper.hidden = false;
    this.emailInput?.focus();
  }

  _hideForm() {
    this.trigger.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.formWrapper.hidden = true;
  }

  async _onSubmit(e) {
    e.preventDefault();

    const email = this.emailInput?.value?.trim();
    if (!email || !this._isValidEmail(email)) {
      this.emailInput?.setCustomValidity('Bitte gib eine gültige E-Mail-Adresse ein.');
      this.emailInput?.reportValidity();
      return;
    }

    this.submitButton.disabled = true;
    this.submitButton.textContent = '…';

    const entry = {
      email,
      productHandle: this.productHandle,
      variantId: this.variantId,
      productTitle: this.productTitle,
      timestamp: new Date().toISOString(),
    };

    // Try external endpoint first (e.g. Klaviyo, custom app)
    const endpoint = window.__bisEndpoint;
    if (endpoint) {
      try {
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: entry.email,
            variant: entry.variantId,
            product: entry.productHandle,
            platform: 'shopify',
          }),
        });
      } catch {
        // Silently fall back to localStorage
      }
    }

    // Always store locally as backup / confirmation check
    this._storeLocally(entry);
    this._showSuccess();
  }

  _showSuccess() {
    if (this.trigger) this.trigger.hidden = true;
    if (this.formWrapper) this.formWrapper.hidden = true;
    if (this.success) this.success.hidden = false;
  }

  _storeLocally(entry) {
    try {
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      // Prevent duplicates
      const isDuplicate = existing.some(
        (e) => e.productHandle === entry.productHandle && e.variantId === entry.variantId && e.email === entry.email
      );
      if (!isDuplicate) {
        existing.push(entry);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
      }
    } catch {
      // localStorage not available — fail silently
    }
  }

  _isAlreadySubscribed() {
    try {
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return existing.some(
        (e) => e.productHandle === this.productHandle && e.variantId === this.variantId
      );
    } catch {
      return false;
    }
  }

  _isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}
